/**
 * useOpportunityAnalysis Hook
 * Fetches comprehensive opportunity analysis using RAG
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAPIClient } from '@tracekit/core';

export interface OpportunityAnalysisRequest {
  include_technical_approaches?: boolean;
  include_past_projects?: boolean;
  include_staff_allocation?: boolean;
  include_resource_estimation?: boolean;
}

export interface TechnicalApproach {
  title: string;
  description: string;
  source: string;
  relevance_score: number;
}

export interface SimilarProject {
  project_name: string;
  relevance_score: number;
  similarity_reasons: string[];
}

export interface StaffAllocation {
  name: string;
  role: string;
  qualifications: string[];
  match_score: number;
}

export interface Citation {
  id?: number;
  source?: string;
  artifact_name?: string;
  artifact_filename?: string;  // From RAG response metadata
  artifact_id?: string;
  chunk_id?: string;
  page?: number;
  section?: string;
  section_path?: string;
  content?: string;
  confidence?: number;
  relevance_score?: number;
  rrf_score?: number;
  rank?: number;
  evidence_id?: string;
}

export interface OpportunityAnalysisResponse {
  capability_match_score: number;
  summary: string;
  technical_approaches: TechnicalApproach[];
  similar_projects: SimilarProject[];
  staff_allocation: StaffAllocation[];
  recommendations: string[];
  citations: Citation[];
  analysis_source: string;
}

/**
 * Hook to fetch comprehensive opportunity analysis
 * 
 * @param opportunityId - UUID of the opportunity entity
 * @param projectId - Project ID
 * @param request - Optional request parameters
 * @param enabled - Whether the query should be enabled (default: true)
 * @param analysis_mode - Analysis mode: 'quick' (fast, essential) or 'deep' (comprehensive)
 * @param force_refresh - If true, bypass cache and run fresh analysis
 * 
 * @returns Query result with analysis data, loading state, and error handling
 */
export function useOpportunityAnalysis(
  opportunityId: string | null | undefined,
  projectId: string | null | undefined,
  request: OpportunityAnalysisRequest = {},
  enabled: boolean = true,
  analysis_mode: 'quick' | 'deep' = 'quick',
  force_refresh: boolean = false
) {
  const api = getAPIClient();

  // Create a stable request object to prevent unnecessary re-fetches
  const stableRequest = React.useMemo(() => ({
    include_technical_approaches: request.include_technical_approaches ?? true,
    include_past_projects: request.include_past_projects ?? true,
    include_staff_allocation: request.include_staff_allocation ?? true,
    include_resource_estimation: request.include_resource_estimation ?? false,
  }), [
    request.include_technical_approaches,
    request.include_past_projects,
    request.include_staff_allocation,
    request.include_resource_estimation,
  ]);

  // Create a stable query key that includes force_refresh to bypass cache when needed
  const queryKey = React.useMemo(() => [
    'opportunity-analysis',
    opportunityId,
    projectId,
    analysis_mode,
    stableRequest,
    force_refresh, // Include in key so cache is invalidated when force_refresh changes
  ], [opportunityId, projectId, analysis_mode, stableRequest, force_refresh]);

  return useQuery<OpportunityAnalysisResponse>({
    queryKey,
    queryFn: async () => {
      if (!opportunityId || !projectId) {
        throw new Error('Opportunity ID and Project ID are required');
      }

      console.log('[useOpportunityAnalysis] Fetching analysis for opportunity:', opportunityId, {
        analysis_mode,
        force_refresh,
      });

      try {
        const response = await api.post<OpportunityAnalysisResponse>(
          `/api/opportunities/${opportunityId}/analyze`,
          stableRequest,
          {
            params: {
              project_id: projectId,
              analysis_mode,
              force_refresh: force_refresh ? 'true' : 'false',
            },
          }
        );

        // API client returns the data directly (not wrapped in response.data)
        const data = response as OpportunityAnalysisResponse;

        // Validate response data
        if (!data || typeof data !== 'object') {
          console.error('[useOpportunityAnalysis] Response data is invalid:', data);
          throw new Error('Invalid response: data is undefined or invalid');
        }

        // Ensure all required fields are present and properly formatted
        const processedData = {
          capability_match_score: typeof data.capability_match_score === 'number' ? data.capability_match_score : 0.0,
          summary: data.summary || 'Analysis in progress.',
          technical_approaches: Array.isArray(data.technical_approaches) ? data.technical_approaches : [],
          similar_projects: Array.isArray(data.similar_projects) ? data.similar_projects : [],
          staff_allocation: Array.isArray(data.staff_allocation) ? data.staff_allocation : [],
          recommendations: Array.isArray(data.recommendations) ? data.recommendations : [],
          citations: Array.isArray(data.citations) ? data.citations : [],
          analysis_source: data.analysis_source || 'unknown',
        };

        console.log('[useOpportunityAnalysis] Successfully processed analysis:', {
          capabilityMatchScore: processedData.capability_match_score,
          technicalApproachesCount: processedData.technical_approaches.length,
          similarProjectsCount: processedData.similar_projects.length,
          staffAllocationCount: processedData.staff_allocation.length,
          recommendationsCount: processedData.recommendations.length,
          citationsCount: processedData.citations.length,
          analysisSource: processedData.analysis_source,
        });

        return processedData;
      } catch (error) {
        console.error('[useOpportunityAnalysis] Error fetching analysis:', error);
        throw error;
      }
    },
    enabled: enabled && !!opportunityId && !!projectId,
    staleTime: 60 * 60 * 1000, // 1 hour: use cache, don't refetch
    gcTime: 24 * 60 * 60 * 1000, // 24 hours: keep in cache across tab/path changes
    retry: 1, // Reduce retries to prevent multiple calls
    retryDelay: 1000,
  });
}

